import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react';

import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';
import { getFileType, remCss, useScreenDetail } from 'src/helpers';
import { MCButton } from '../../basic';
import LANG_CONST from 'src/helpers/lang/const';
import { Splide, SplideSlide } from '@splidejs/react-splide';

function centerAspectCrop(mediaWidth, mediaHeight, aspect = 1) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const Item = forwardRef(({ src, aspect = 1 }, ref) => {
  const screenDetail = useScreenDetail();
  const [img, setImg] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [size, setSize] = useState({ width: '100%', height: '100%' });
  const imgRef = useRef(null);

  const onImageLoad = useCallback(
    (width, height) => {
      let scale = Math.min(
        (screenDetail.innerWidth * 0.8) / width,
        (screenDetail.innerHeight * 0.8 - 100) / height
      );

      setSize({
        width: width * scale,
        height: height * scale,
        orgWidth: width,
        orgHeight: height,
        scale
      });
      setCrop(centerAspectCrop(width * scale, height * scale, aspect));
    },
    [aspect, screenDetail.innerHeight, screenDetail.innerWidth]
  );

  const toFile = useCallback(
    (e) => {
      e.preventDefault();
      const canvas = document.createElement('canvas');
      const pixelCrop = completedCrop;
      canvas.width = pixelCrop.width / size.scale;
      canvas.height = pixelCrop.height / size.scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(
        imgRef.current,
        pixelCrop.x / size.scale,
        pixelCrop.y / size.scale,
        pixelCrop.width / size.scale,
        pixelCrop.height / size.scale,
        0,
        0,
        pixelCrop.width / size.scale,
        pixelCrop.height / size.scale
      );

      return new Promise((resolve) =>
        canvas.toBlob((b) => {
          canvas.remove();
          const arr = src.name.split('.');
          arr[arr.length - 1] = 'jpeg';
          resolve(new File([b], arr.join('.'), { type: 'image/jpeg' }));
        }, 'image/jpeg')
      );
    },
    [completedCrop, size, src.name]
  );

  useImperativeHandle(ref, () => ({
    save(e) {
      return toFile(e);
    }
  }));

  useEffect(() => {
    let ignore = false;
    let temp = new Image();
    let objectUrl = URL.createObjectURL(src);
    temp.onload = function () {
      if (ignore) return;

      setImg(objectUrl);
      onImageLoad(this.width, this.height);
    };
    temp.src = objectUrl;

    return () => {
      ignore = true;
      URL.revokeObjectURL(objectUrl);
    };
  }, [onImageLoad, src]);
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <ReactCrop
        crop={crop}
        onChange={(c) => setCrop(c)}
        aspect={aspect}
        keepSelection={true}
        onComplete={(c) => setCompletedCrop(c)}
      >
        <img src={img} width={size.width} height={size.height} ref={imgRef} />
      </ReactCrop>
    </div>
  );
});
Item.displayName = 'Item';

const CropImage = ({ src, hide, onSave, aspect = 1 }) => {
  const refs = useRef([]);

  const save = async (e) => {
    if (Array.isArray(src)) {
      onSave(
        await Promise.all(
          src.map((m, i) => {
            if (refs.current[i]) return refs.current[i].save(e);
            return m;
          })
        )
      );
      hide();
    } else {
      onSave(await refs.current[0].save(e));
      hide();
    }
  };

  return (
    <div
      className='mc-popup-contain'
      style={{
        alignSelf: 'center',
        borderRadius: remCss(20),
        width: 'auto',
        height: 'auto'
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        <Splide options={{ rewind: true }}>
          {(Array.isArray(src) ? src : [src]).map(
            (m, i) =>
              getFileType(m) === 'image' && (
                <SplideSlide key={i}>
                  <Item src={m} ref={(ref) => (refs.current[i] = ref)} aspect={aspect} />
                </SplideSlide>
              )
          )}
        </Splide>
        <div className={'mc-flex mc-mt20'} style={{ justifyContent: 'flex-center' }}>
          <MCButton
            radius
            primary
            quaternary
            onClick={hide}
            light
            label={LANG_CONST.buttons.cancel}
          />
          <MCButton primary radius onClick={save} dark label={LANG_CONST.buttons.upload} />
        </div>
      </div>
    </div>
  );
};

CropImage.displayName = 'CropImage';

export default CropImage;
